import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../state/language/LanguageProvider';
import { emailSettingsService, EmailSettings } from '../../services/emailSettingsService';
import { 
  CogIcon, 
  CheckCircleIcon,
  XCircleIcon,
  BeakerIcon
} from '@heroicons/react/24/outline';


export const EmailSettingsPage: React.FC = () => {
  const { t } = useLanguage();
  const [settings, setSettings] = useState<EmailSettings>({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_encryption: 'tls',
    from_name: '',
    from_email: '',
    reply_to_email: '',
    is_active: false
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Load existing settings on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await emailSettingsService.getSettings();
      if (response) {
        setSettings(response);
      }
    } catch (error) {
      console.error('Failed to load email settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof EmailSettings, value: string | number | boolean) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate required fields
      if (!settings.smtp_host || !settings.smtp_username || !settings.from_email) {
        setError(t('crm.emailSettings.requiredFields'));
        return;
      }

      await emailSettingsService.saveSettings(settings);
      setSuccess(t('crm.emailSettings.savedSuccessfully'));
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message :
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        t('crm.emailSettings.saveError');
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const result = await emailSettingsService.testConnection(settings);
      setTestResult(result);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message :
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        t('crm.emailSettings.testError');
      setTestResult({
        success: false,
        message: errorMessage
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Settings Form */}
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {t('crm.emailSettings.smtpConfiguration')}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {t('crm.emailSettings.smtpDescription')}
              </p>
            </div>

            <div className="px-6 py-6">
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex">
                    <XCircleIcon className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {success && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
                  <div className="flex">
                    <CheckCircleIcon className="h-5 w-5 text-green-400" />
                    <div className="ml-3">
                      <p className="text-sm text-green-600">{success}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* SMTP Host */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('crm.emailSettings.smtpHost')} *
                  </label>
                  <input
                    type="text"
                    value={settings.smtp_host}
                    onChange={(e) => handleInputChange('smtp_host', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="smtp.gmail.com"
                    required
                  />
                </div>

                {/* SMTP Port */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('crm.emailSettings.smtpPort')} *
                  </label>
                  <input
                    type="number"
                    value={settings.smtp_port}
                    onChange={(e) => handleInputChange('smtp_port', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="587"
                    required
                  />
                </div>

                {/* SMTP Username */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('crm.emailSettings.smtpUsername')} *
                  </label>
                  <input
                    type="text"
                    value={settings.smtp_username}
                    onChange={(e) => handleInputChange('smtp_username', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="your-email@gmail.com"
                    required
                  />
                </div>

                {/* SMTP Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('crm.emailSettings.smtpPassword')} *
                  </label>
                  <input
                    type="password"
                    value={settings.smtp_password}
                    onChange={(e) => handleInputChange('smtp_password', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                    required
                  />
                </div>

                {/* SMTP Encryption */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('crm.emailSettings.smtpEncryption')} *
                  </label>
                  <select
                    value={settings.smtp_encryption}
                    onChange={(e) => handleInputChange('smtp_encryption', e.target.value as 'none' | 'tls' | 'ssl')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="none">{t('crm.emailSettings.encryptionNone')}</option>
                    <option value="tls">{t('crm.emailSettings.encryptionTLS')}</option>
                    <option value="ssl">{t('crm.emailSettings.encryptionSSL')}</option>
                  </select>
                </div>

                {/* From Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('crm.emailSettings.fromName')} *
                  </label>
                  <input
                    type="text"
                    value={settings.from_name}
                    onChange={(e) => handleInputChange('from_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('crm.emailSettings.companyNamePlaceholder')}
                    required
                  />
                </div>

                {/* From Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('crm.emailSettings.fromEmail')} *
                  </label>
                  <input
                    type="email"
                    value={settings.from_email}
                    onChange={(e) => handleInputChange('from_email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="noreply@yourcompany.com"
                    required
                  />
                </div>

                {/* Reply To Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('crm.emailSettings.replyToEmail')}
                  </label>
                  <input
                    type="email"
                    value={settings.reply_to_email}
                    onChange={(e) => handleInputChange('reply_to_email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="support@yourcompany.com"
                  />
                </div>
              </div>

              {/* Active Toggle */}
              <div className="mt-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {t('crm.emailSettings.isActive')}
                  </span>
                </label>
                <p className="mt-1 text-sm text-gray-500">
                  {t('crm.emailSettings.isActiveDescription')}
                </p>
              </div>

              {/* Test Connection */}
              <div className="mt-6 p-4 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  {t('crm.emailSettings.testConnection')}
                </h4>
                <p className="text-sm text-gray-500 mb-4">
                  {t('crm.emailSettings.testConnectionDescription')}
                </p>
                
                {testResult && (
                  <div className={`mb-4 p-3 rounded-md ${
                    testResult.success 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    <div className="flex">
                      {testResult.success ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-400" />
                      ) : (
                        <XCircleIcon className="h-5 w-5 text-red-400" />
                      )}
                      <div className="ml-3">
                        <p className={`text-sm ${
                          testResult.success ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {testResult.message}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleTestConnection}
                  disabled={testing || !settings.smtp_host || !settings.smtp_username}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <BeakerIcon className="h-4 w-4 mr-2" />
                  {testing ? t('crm.emailSettings.testing') : t('crm.emailSettings.testConnection')}
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <CogIcon className="h-4 w-4 mr-2" />
                  {saving ? t('common.saving') : t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-blue-50 border border-blue-200 rounded-md p-6">
            <h3 className="text-lg font-medium text-blue-900 mb-2">
              {t('crm.emailSettings.helpTitle')}
            </h3>
            <div className="text-sm text-blue-700 space-y-2">
              <p>{t('crm.emailSettings.helpGmail')}</p>
              <p>{t('crm.emailSettings.helpOutlook')}</p>
              <p>{t('crm.emailSettings.helpCustom')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
